import { useActiveProfile } from '../../../contexts/ActiveBusinessContext';
import { Loader2, Building, Phone, Globe, MapPin, Info } from 'lucide-react';

export default function CompanyDetailsTab() {
  const { profile, loading } = useActiveProfile();

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm text-slate-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const websiteUrl = profile?.website || '';
  const hasWebsite = websiteUrl.length > 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900">Company Information</h2>
        <p className="mt-1 text-sm text-slate-500">
          Your company details as they appear to customers.
        </p>
      </div>

      {/* Google Places Notice */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="mt-0.5 rounded-full bg-blue-100 p-1">
          <Info className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-medium text-blue-800">Google Places Synced Business</h3>
          <p className="mt-1 text-sm text-blue-700">
            This business information is sourced from Google Places. To update your listing, please update your Google Business Profile.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Company Information Section */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Building className="h-4 w-4 text-slate-500" />
              Company Details
            </h3>
          </div>

          <div className="p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-700">Company Name</p>
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-slate-400" />
                  <p className="text-sm font-bold text-slate-900">
                    {profile?.company_name || '-'}
                  </p>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-700">Contact Telephone Number</p>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <p className="text-sm text-slate-900">
                    {profile?.telephone_number || '-'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <p className="text-sm font-medium text-slate-700">Website</p>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-400" />
                  {hasWebsite ? (
                    <a
                      href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {websiteUrl}
                    </a>
                  ) : (
                    <p className="text-sm text-slate-900">-</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <MapPin className="h-4 w-4 text-slate-500" />
              Business Address
            </h3>
          </div>

          <div className="p-4">
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="text-sm text-slate-900 leading-relaxed">
                {profile?.address_line1 && <p>{profile.address_line1}</p>}
                {profile?.address_line2 && <p>{profile.address_line2}</p>}
                {(profile?.city || profile?.county) && (
                  <p>
                    {[profile?.city, profile?.county].filter(Boolean).join(', ')}
                  </p>
                )}
                {profile?.postcode && <p>{profile.postcode}</p>}
                {profile?.country && <p>{profile.country}</p>}
                {!profile?.address_line1 && !profile?.city && !profile?.postcode && (
                  <p className="text-slate-500">No address on file</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
